import React, { useCallback, useEffect } from 'react'
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
} from '@chakra-ui/react'
import { ChevronDownIcon } from '@chakra-ui/icons'
import { observer } from 'mobx-react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toUpper } from 'lodash'
import rootStore from '../../../store/RootStore'

/** Picker for the saved snapshots of the PABS live report. */
// The live view is deliberately absent from this list. It stays served and routable; the product
// just does not put it in front of a reader.
const SnapshotControls = observer(() => {
  const {
    reportStore: { snapshots, fetchSnapshots, loadingSnapshots },
    productsStore: { currentProduct },
    planStore: { currentPlan },
  } = rootStore

  const { reportId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const currentSnapshotId = searchParams.get('snapshot') || undefined

  const isPabsLive =
    currentProduct === 'pa' && toUpper(reportId || '') === 'LIVEREPORT'

  useEffect(() => {
    if (isPabsLive) fetchSnapshots()
  }, [isPabsLive, fetchSnapshots, currentPlan?.planId])

  const goTo = useCallback(
    snapshotId => {
      navigate(`/reports/${reportId}?snapshot=${snapshotId}`)
    },
    [navigate, reportId],
  )

  const formatDate = iso => {
    if (!iso) return ''
    const d = new Date(iso)
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString()
  }

  if (!isPabsLive) return null

  const activeLabel = currentSnapshotId
    ? snapshots.find(s => s.snapshotId === currentSnapshotId)?.name ?? 'Snapshot'
    : 'Live'

  return (
    <Menu onOpen={fetchSnapshots}>
      <MenuButton
        as={Button}
        variant="outline"
        borderRadius="full"
        rightIcon={<ChevronDownIcon />}
        mt="8px"
        ml="-8px"
        data-testid="snapshot-picker">
        {activeLabel}
      </MenuButton>
      <Portal>
        <MenuList
          maxH="320px"
          overflowY="auto"
          zIndex={3}>
          {loadingSnapshots && <MenuItem isDisabled>Loading…</MenuItem>}
          {!loadingSnapshots && snapshots.length === 0 && (
            <MenuItem isDisabled>No snapshots yet</MenuItem>
          )}
          {snapshots.map(s => (
            <MenuItem
              key={s.snapshotId}
              onClick={() => goTo(s.snapshotId)}
              fontWeight={s.snapshotId === currentSnapshotId ? 'bold' : 'normal'}>
              {s.name}
              {formatDate(s.createdAtUtc) ? ` · ${formatDate(s.createdAtUtc)}` : ''}
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  )
})

SnapshotControls.displayName = 'SnapshotControls'

export default SnapshotControls
